import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Award, Download, FileText } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const WorkerCertificates = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/certificates');
      setCertificates(response.data);
    } catch (error) {
      console.error('Failed to load certificates:', error);
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleViewFile = (cert) => {
    const token = localStorage.getItem('token');
    window.open(`${axiosInstance.defaults.baseURL}/certificates/${cert.id}/download-file?token=${token}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className="mb-4 hover:bg-gray-100"
        >
          &larr; Back
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Certificates</h1>
          <p className="text-gray-600 mt-1">View and download your training certificates</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          </div>
        ) : certificates.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Award className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Certificates Yet</h3>
              <p className="text-slate-500">Complete courses and pass quizzes to earn certificates</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {certificates.map(cert => (
              <Card key={cert.id} data-testid={`worker-cert-card-${cert.id}`} className="card-hover bg-gradient-to-br from-orange-50 to-yellow-50 border-2 border-orange-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <Award className="w-12 h-12 text-orange-500" />
                    <Badge className="bg-green-100 text-green-800">{cert.score}%</Badge>
                  </div>
                  <CardTitle className="text-xl mt-4">{cert.courseName}</CardTitle>
                  <CardDescription>Certificate ID: {cert.certificateId}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 text-sm text-slate-600">
                    <p>Issued to: {cert.userName}</p>
                    <p>Date: {new Date(cert.issuedAt).toLocaleDateString()}</p>
                    {cert.expiresAt && (
                      <p>Expires: {new Date(cert.expiresAt).toLocaleDateString()}</p>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col gap-2">
                  <Button
                    data-testid={`view-cert-${cert.id}`}
                    className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
                    onClick={() => navigate('/certificates')}
                  >
                    View Certificate
                  </Button>
                  {cert.attachedFileId && (
                    <Button
                      data-testid={`download-cert-file-${cert.id}`}
                      variant="outline"
                      className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                      onClick={() => handleViewFile(cert)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Original Certificate
                      {cert.attachedFileName && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {cert.attachedFileName.split('.').pop()?.toUpperCase()}
                        </Badge>
                      )}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerCertificates;